import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { trackActivity } from '../../services/api';
import FilterPanel from './FilterPanel';
import MetricPicker from './MetricPicker';
import ThresholdEditor from './ThresholdEditor';
import ResultsGrid from './ResultsGrid';
import ChartPanel from './ChartPanel';
import ScoreBuilder from './ScoreBuilder';
import AnalysisTab from './AnalysisTab';
import HeldManagersTab from './HeldManagersTab';
import { COLUMN_SETS, PERIOD_OPTIONS } from './presets';
import { pillarColour } from './palette';
import { statisticsWeighting, toDraft } from './weighting';
import { errorText, longDate, metricHeading, metricKey, monthLabel, sortRows, thresholdsForApi } from './format';
import './QuantScreenerApp.css';

// Quant Screener (Drummond-only). Statistic x period x rolling window over the monthly
// return history, percentiles within the Sub Asset Class, pillar-weighted scores with
// saved weighting sets (one Drummond Standard), threshold rules, in-grid graphics, Excel.

const EMPTY_FILTERS = {
  sub_asset_classes: [], asset_class_groups: [], styles: [], tilts: [], platforms: [],
  held: null, apl: null, flagship: null, hedged: null, min_months: '', search: '',
};
const SPARK = { statistic: 'active_return', period: '3y', rolling: 12 };

const cleanFilters = (f) => {
  const out = {};
  ['sub_asset_classes', 'asset_class_groups', 'styles', 'tilts', 'platforms'].forEach((k) => { if (f[k]?.length) out[k] = f[k]; });
  ['held', 'apl', 'flagship', 'hedged'].forEach((k) => { if (f[k] !== null && f[k] !== undefined) out[k] = f[k]; });
  if (f.min_months !== '' && f.min_months !== null && f.min_months !== undefined) out.min_months = Number(f.min_months);
  if (f.search?.trim()) out.search = f.search.trim();
  return out;
};

export default function QuantScreenerApp() {
  const [catalogue, setCatalogue] = useState(null);
  const [universe, setUniverse] = useState(null);
  const [sets, setSets] = useState([]);
  const [loadError, setLoadError] = useState(null);

  // Screen definition.
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [columnSet, setColumnSet] = useState('core');
  const [period, setPeriod] = useState('3y');
  const [fromMonth, setFromMonth] = useState('');          // From month-end when period is "custom"
  const [customMetrics, setCustomMetrics] = useState(COLUMN_SETS.core.build('3y'));
  const [thresholds, setThresholds] = useState([]);
  const [asAt, setAsAt] = useState('');
  const [scoreMode, setScoreMode] = useState('standard');   // 'none' | 'standard' | '<id>' | 'draft' | 'stats'
  // "Statistics" score mode: several statistics with ONE shared window; each becomes a
  // column and the score is the equal-weighted average of their percentiles.
  const [statsPick, setStatsPick] = useState({ statistics: ['information_ratio'], period: '3y', start: '', rolling: null, summary: 'latest' });
  const [draft, setDraft] = useState(null);
  const [historyMonths, setHistoryMonths] = useState(36);
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [sparkOn, setSparkOn] = useState(true);
  const [heat, setHeat] = useState(true);
  const [requireAll, setRequireAll] = useState(false);
  const [requireFullScore, setRequireFullScore] = useState(false);

  // Screen view.
  const [tab, setTab] = useState('screen');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [chartsOpen, setChartsOpen] = useState(false);
  const [moreCols, setMoreCols] = useState(false);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [sort, setSort] = useState({ key: 'score', dir: 'desc' });
  const [hiddenCols, setHiddenCols] = useState(() => new Set());
  const [distribution, setDistribution] = useState(null);
  const [exporting, setExporting] = useState(false);

  const statsSpecs = useMemo(() => statsPick.statistics.map((s) => ({
    statistic: s, period: statsPick.period, start: statsPick.period === 'custom' ? statsPick.start : undefined,
    rolling: statsPick.rolling || null, summary: statsPick.rolling ? (statsPick.summary || 'latest') : 'latest',
  })), [statsPick]);
  const customReady = scoreMode === 'stats'
    ? (statsPick.period !== 'custom' || !!statsPick.start) && statsPick.statistics.length > 0
    : (period !== 'custom' || !!fromMonth);
  const metrics = useMemo(() => {
    if (scoreMode === 'stats') return statsSpecs;
    return columnSet === 'custom' ? customMetrics : COLUMN_SETS[columnSet].build(period, fromMonth);
  }, [scoreMode, statsSpecs, columnSet, period, fromMonth, customMetrics]);
  const metricKeys = useMemo(() => metrics.map(metricKey), [metrics]);
  const liveThresholds = useMemo(() => thresholds.filter((t) => metricKeys.includes(t.metric)), [thresholds, metricKeys]);

  const reloadSets = useCallback(async () => {
    const r = await api.get('/api/quant-screener/weighting-sets');
    setSets(r.data);
    return r.data;
  }, []);

  useEffect(() => {
    trackActivity('app_access', 'quant_screener');
    (async () => {
      try {
        const [c, u] = await Promise.all([api.get('/api/quant-screener/catalogue'), api.get('/api/quant-screener/universe')]);
        setCatalogue(c.data); setUniverse(u.data);
        await reloadSets();
      } catch (e) { setLoadError(errorText(e)); }
    })();
  }, [reloadSets]);

  // The scoring part of a request: a saved set by id, the inline draft, or one statistic.
  const scoring = useMemo(() => {
    if (scoreMode === 'stats') {
      return statsSpecs.length ? { weighting: statisticsWeighting(statsSpecs) } : {};
    }
    if (scoreMode === 'draft' && draft) {
      return { weighting: {
        name: draft.name || 'Draft', description: draft.description, score_method: draft.score_method,
        pillars: draft.pillars.map((p) => ({ name: p.name, weight: Number(p.weight), metrics: p.metrics.map((m) => ({
          statistic: m.statistic, period: m.period, rolling: m.rolling || null, summary: m.summary || 'latest', weight: Number(m.weight), label: m.label || undefined,
        })) })),
      } };
    }
    if (scoreMode === 'standard') {
      const std = sets.find((s) => s.is_standard);
      return std ? { weighting_set_id: std.id } : {};
    }
    if (scoreMode !== 'none') return { weighting_set_id: Number(scoreMode) };
    return {};
  }, [scoreMode, draft, sets, statsSpecs]);

  // The composition shown on the Screen tab for whatever is scoring it.
  const activeSet = useMemo(() => {
    if (scoreMode === 'none') return null;
    if (scoreMode === 'stats') return scoring.weighting || null;
    if (scoreMode === 'draft') return draft;
    if (scoreMode === 'standard') return sets.find((s) => s.is_standard) || null;
    return sets.find((s) => String(s.id) === scoreMode) || null;
  }, [scoreMode, scoring, draft, sets]);
  const editActiveSet = () => {
    if (activeSet && scoreMode !== 'draft') setDraft(toDraft(scoreMode === 'stats' ? { ...activeSet, id: undefined } : activeSet));
    setTab('score');
  };

  const body = useMemo(() => ({
    filters: cleanFilters(filters),
    metrics: metrics.map((m) => ({ statistic: m.statistic, period: m.period, rolling: m.rolling || null, summary: m.summary || 'latest' })),
    thresholds: thresholdsForApi(liveThresholds, metrics, catalogue),
    as_at: asAt || null,
    sparkline: sparkOn ? SPARK : null,
    require_all_columns: requireAll,
    require_full_score: requireFullScore,
    ...scoring,
  }), [filters, metrics, liveThresholds, asAt, sparkOn, requireAll, requireFullScore, scoring, catalogue]);

  const runScreen = useCallback(async () => {
    setRunning(true); setError(null);
    try {
      const r = await api.post('/api/quant-screener/screen', body);
      setResult(r.data);
      setSort((s) => (r.data.weighting ? s : (s.key === 'score' || s.key === 'rank' ? { key: r.data.metrics[0] ? `${r.data.metrics[0].key}:percentile` : 'name', dir: 'desc' } : s)));
      setTab('screen');
    } catch (e) { setError(errorText(e)); } finally { setRunning(false); }
  }, [body]);

  // First run once the sets are known (so the standard scores it).
  const [booted, setBooted] = useState(false);
  useEffect(() => {
    if (!booted && catalogue && universe && sets.length) { setBooted(true); runScreen(); }
  }, [booted, catalogue, universe, sets, runScreen]);

  // Cohort distribution for the strip chart when exactly one sub asset class is on screen.
  const sortedMetricKey = sort.key.includes(':') ? sort.key.split(':')[0] : result?.metrics?.[0]?.key;
  const singleSac = useMemo(() => {
    if (!result) return null;
    const sacs = new Set(result.rows.map((r) => r.sub_asset_class));
    return sacs.size === 1 ? [...sacs][0] : null;
  }, [result]);
  useEffect(() => {
    if (!chartsOpen || !result || !singleSac || !sortedMetricKey) { setDistribution(null); return undefined; }
    const m = result.metrics.find((x) => x.key === sortedMetricKey);
    if (!m) { setDistribution(null); return undefined; }
    let cancelled = false;
    api.post('/api/quant-screener/analysis/distribution', {
      metric: { statistic: m.statistic, period: m.period, rolling: m.rolling, summary: m.summary },
      sub_asset_class: singleSac, as_at: result.as_at,
    }).then((r) => { if (!cancelled) setDistribution({ ...r.data, metric: { ...r.data.metric, key: m.key } }); })
      .catch(() => { if (!cancelled) setDistribution(null); });
    return () => { cancelled = true; };
  }, [chartsOpen, result, singleSac, sortedMetricKey]);

  // Rows on screen get a screen rank (1..N by total score, nulls last) that does not
  // depend on the current sort; the cohort rank from the API stays as "SAC rank".
  // History (statistics and pillar scores over time) for the selected funds, when the charts are open.
  useEffect(() => {
    if (!chartsOpen || !result || selected.size === 0) { setHistory(null); setHistoryError(null); return undefined; }
    const funds = [...selected].slice(0, 12);
    let cancelled = false;
    setHistoryError(null);
    api.post('/api/quant-screener/analysis/history', {
      funds, metrics: result.metrics.map((m) => ({ statistic: m.statistic, period: m.period, start: m.start || undefined, rolling: m.rolling, summary: m.summary })),
      as_at: result.as_at, months: historyMonths, ...scoring,
    }).then((r) => { if (!cancelled) setHistory(r.data); })
      .catch((e) => { if (!cancelled) { setHistory(null); setHistoryError(errorText(e)); } });
    return () => { cancelled = true; };
  }, [chartsOpen, result, selected, historyMonths, scoring]);

  const rows = useMemo(() => {
    if (!result) return [];
    const byScore = sortRows(result.rows, { key: 'score', dir: 'desc' });
    const rankOf = new Map(byScore.map((r, i) => [r.tr_code, r.score?.total === null || r.score?.total === undefined ? null : i + 1]));
    const withRank = result.rows.map((r) => ({ ...r, screen_rank: rankOf.get(r.tr_code) ?? null }));
    return sortRows(withRank, sort);
  }, [result, sort]);
  const toggleSelect = (tr) => setSelected((s) => { const n = new Set(s); if (n.has(tr)) n.delete(tr); else n.add(tr); return n; });
  const toggleCol = (key) => setHiddenCols((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const exportExcel = async () => {
    setExporting(true); setError(null);
    try {
      const r = await api.post('/api/quant-screener/export.xlsx', body, { responseType: 'blob' });
      const m = /filename="([^"]+)"/.exec(r.headers?.['content-disposition'] || '');
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = m ? m[1] : 'quant-screener.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.message;
      try { detail = JSON.parse(await e.response?.data?.text())?.detail || detail; } catch { /* keep message */ }
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally { setExporting(false); }
  };

  const scoreLabel = scoreMode === 'none' ? 'no score' : scoreMode === 'stats' ? `${statsPick.statistics.length} statistic${statsPick.statistics.length === 1 ? '' : 's'} (equal weight)`
    : scoreMode === 'draft' ? `draft${draft?.name ? ': ' + draft.name : ''}`
      : scoreMode === 'standard' ? (sets.find((s) => s.is_standard)?.name || 'Drummond Standard') : (sets.find((s) => String(s.id) === scoreMode)?.name || 'set');
  const staleRules = thresholds.length - liveThresholds.length;

  return (
    <div className="qs-app">
      <div className="qs-head">
        <h2>Quant Screener</h2>
        <span className="qs-muted">Statistics over monthly returns · percentiles within the sub asset class · scores from a weighting set · Drummond only</span>
        {catalogue && <span className="qs-muted">Data to {longDate(catalogue.data_max_date)} · risk-free: {catalogue.risk_free.label}</span>}
      </div>
      {loadError && <div className="qs-alert">{loadError}</div>}

      <div className="qs-tabs">
        <button type="button" className={`qs-tab${tab === 'screen' ? ' active' : ''}`} onClick={() => setTab('screen')}>Screen</button>
        <button type="button" className={`qs-tab${tab === 'analysis' ? ' active' : ''}`} onClick={() => setTab('analysis')}>Analysis{selected.size ? ` (${selected.size} selected)` : ''}</button>
        <button type="button" className={`qs-tab${tab === 'held' ? ' active' : ''}`} onClick={() => setTab('held')}>Our manager selection</button>
        <button type="button" className={`qs-tab${tab === 'score' ? ' active' : ''}`} onClick={() => setTab('score')}>Score builder</button>
        <span className="qs-tab-note">Scoring with: <b>{scoreLabel}</b>{asAt ? <> · as at <b>{longDate(asAt)}</b> (restated)</> : ''}</span>
      </div>

      {tab === 'score' && (
        <ScoreBuilder catalogue={catalogue} universe={universe} sets={sets} reloadSets={reloadSets} scoreMode={scoreMode} setScoreMode={setScoreMode}
          draft={draft} setDraft={setDraft} onApplyDraft={() => { setTab('screen'); }} />
      )}
      {tab === 'analysis' && (
        <AnalysisTab catalogue={catalogue} universe={universe} asAt={asAt} defaultSac={singleSac || filters.sub_asset_classes[0] || ''}
          selectedCodes={[...selected]} selectedNames={rows.filter((r) => selected.has(r.tr_code)).map((r) => r.name || r.tr_code)} />
      )}
      {tab === 'held' && (
        <HeldManagersTab catalogue={catalogue} scoring={scoring} asAt={asAt} />
      )}

      {tab === 'screen' && (
        <div className="qs-layout">
          <aside className="qs-side">
            <FilterPanel universe={universe} filters={filters} setFilters={setFilters} />
          </aside>
          <section className="qs-main">
            <div className="qs-controls">
              <label className="qs-ctl">Columns
                <select value={scoreMode === 'stats' ? 'stats' : columnSet} disabled={scoreMode === 'stats'} title={scoreMode === 'stats' ? 'The chosen statistics are the columns' : ''}
                  onChange={(e) => { const v = e.target.value; setColumnSet(v); if (v === 'custom') setCustomMetrics(metrics); if (v === 'custom') setAdvancedOpen(true); }}>
                  {Object.entries(COLUMN_SETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  {scoreMode === 'stats' && <option value="stats">the chosen statistics</option>}
                </select>
              </label>
              {scoreMode !== 'stats' && (
                <label className="qs-ctl">Period
                  <select value={period} onChange={(e) => setPeriod(e.target.value)} disabled={columnSet === 'custom'} title={columnSet === 'custom' ? 'Set the period per column under Advanced' : 'Window for the preset columns; "custom" = between two month-ends'}>
                    {PERIOD_OPTIONS.map((p) => <option key={p} value={p}>{p === 'custom' ? 'custom (between month-ends)' : p}</option>)}
                  </select>
                </label>
              )}
              {scoreMode !== 'stats' && period === 'custom' && columnSet !== 'custom' && (
                <label className="qs-ctl">From
                  <select value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} title="Start month-end; As at is the end month">
                    <option value="">choose…</option>
                    {(universe?.as_at_dates || []).map((d) => <option key={d} value={d}>{monthLabel(d)}</option>)}
                  </select>
                </label>
              )}
              <label className="qs-ctl">{(scoreMode === 'stats' ? statsPick.period : period) === 'custom' ? 'To' : 'As at'}
                <select value={asAt} onChange={(e) => setAsAt(e.target.value)}>
                  <option value="">latest{universe ? ` (${monthLabel(universe.data_max_date)})` : ''}</option>
                  {(universe?.as_at_dates || []).slice(1).map((d) => <option key={d} value={d}>{monthLabel(d)}</option>)}
                </select>
              </label>
              <label className="qs-ctl">Score
                <select value={scoreMode} onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'new') {
                    // Start an empty set in the Score builder; the draft scores the screen once applied.
                    setDraft({ name: '', description: '', score_method: 'percentile', pillars: [] });
                    setScoreMode('draft');
                    setTab('score');
                    return;
                  }
                  setScoreMode(v);
                }}>
                  <option value="none">none</option>
                  {sets.map((s) => <option key={s.id} value={s.is_standard ? 'standard' : String(s.id)}>{s.is_standard ? '★ ' : ''}{s.name}</option>)}
                  {draft && <option value="draft">draft{draft.name ? `: ${draft.name}` : ''}</option>}
                  <option value="stats">statistics… (pick one or more)</option>
                  <option value="new">New set… (opens the Score builder)</option>
                </select>
              </label>
              <button type="button" className="qs-btn qs-btn-primary" onClick={runScreen} disabled={running || !catalogue || !customReady} title={customReady ? '' : 'Choose the From month first'}>{running ? 'Running…' : 'Run'}</button>
              <button type="button" className="qs-btn qs-btn-ghost" onClick={exportExcel} disabled={exporting || !result}>{exporting ? 'Exporting…' : 'Excel'}</button>
              <button type="button" className="qs-link" onClick={() => setAdvancedOpen((o) => !o)}>
                {advancedOpen ? '▾' : '▸'} Advanced{liveThresholds.length || requireAll || requireFullScore ? ` (${[liveThresholds.length ? `${liveThresholds.length} rule${liveThresholds.length > 1 ? 's' : ''}` : null, requireAll ? 'all columns' : null, requireFullScore ? 'full score' : null].filter(Boolean).join(', ')})` : ''}
              </button>
              {result && <span className="qs-count-big"><b>{result.counts.after_thresholds}</b> of {result.counts.universe} pass</span>}
            </div>

            {scoreMode === 'stats' && (
              <div className="qs-composition">
                <div className="qs-comp-head">
                  <span className="qs-block-title">Score by statistics</span>
                  <span className="qs-muted">each chosen statistic becomes a column with a chart; the score is the equal-weighted average of their percentiles (one statistic = its percentile)</span>
                  {statsPick.statistics.length > 0 && <button type="button" className="qs-link" onClick={editActiveSet}>set weights in Score builder</button>}
                </div>
                <div className="qs-metric-form">
                  <label className="qs-ctl">Window
                    <select value={statsPick.period} onChange={(e) => setStatsPick({ ...statsPick, period: e.target.value })}>
                      {(catalogue?.periods || []).map((p) => <option key={p} value={p}>{p === 'si' ? 'since inception' : p === 'custom' ? 'custom (from…)' : p}</option>)}
                    </select>
                  </label>
                  {statsPick.period === 'custom' && (
                    <label className="qs-ctl">From
                      <select value={statsPick.start} onChange={(e) => setStatsPick({ ...statsPick, start: e.target.value })}>
                        <option value="">choose…</option>
                        {(universe?.as_at_dates || []).map((d) => <option key={d} value={d}>{monthLabel(d)}</option>)}
                      </select>
                    </label>
                  )}
                  <label className="qs-ctl">Rolling
                    <select value={statsPick.rolling || ''} onChange={(e) => setStatsPick({ ...statsPick, rolling: e.target.value ? Number(e.target.value) : null })}>
                      <option value="">point</option>
                      {(catalogue?.rolling_windows || []).map((w) => <option key={w} value={w}>rolling {w}m</option>)}
                    </select>
                  </label>
                  <label className="qs-ctl">Summary
                    <select value={statsPick.summary} onChange={(e) => setStatsPick({ ...statsPick, summary: e.target.value })} disabled={!statsPick.rolling}>
                      {(catalogue?.summaries || ['latest']).map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                    </select>
                  </label>
                </div>
                <div className="qs-stats-pick">
                  {(catalogue?.statistics || []).filter((s) => s.direction !== 0).map((s) => (
                    <label key={s.key} className="qs-check" title={s.description}>
                      <input type="checkbox" checked={statsPick.statistics.includes(s.key)}
                        onChange={() => setStatsPick({ ...statsPick, statistics: statsPick.statistics.includes(s.key) ? statsPick.statistics.filter((k) => k !== s.key) : [...statsPick.statistics, s.key] })} />
                      <span className="qs-check-label">{s.label}</span>
                    </label>
                  ))}
                </div>
                {statsPick.statistics.length === 0 && <div className="qs-muted">Pick at least one statistic.</div>}
              </div>
            )}
            {activeSet && scoreMode !== 'stats' && (
              <div className="qs-composition">
                <div className="qs-comp-head">
                  <span className="qs-block-title">Score: {activeSet.name}{scoreMode === 'draft' ? ' (draft, unsaved)' : activeSet.is_standard ? ' ★ Drummond Standard' : ''}</span>
                  <span className="qs-muted">{activeSet.score_method || 'percentile'} · weights shown as shares</span>
                  <button type="button" className="qs-link" onClick={editActiveSet}>{scoreMode === 'draft' ? 'edit in Score builder' : 'edit a copy in Score builder'}</button>
                </div>
                <div className="qs-comp-pillars">
                  {(activeSet.pillars || []).map((p, i) => {
                    const pt = (activeSet.pillars || []).reduce((a, x) => a + (Number(x.weight) || 0), 0) || 1;
                    const mt = (p.metrics || []).reduce((a, x) => a + (Number(x.weight) || 0), 0) || 1;
                    return (
                      <div key={`${p.name}-${i}`} className="qs-comp-pillar" style={{ borderLeftColor: pillarColour(i) }}>
                        <div className="qs-comp-pname">{p.name} <b>{Math.round((Number(p.weight) || 0) / pt * 100)}%</b></div>
                        {(p.metrics || []).map((m, j) => (
                          <div key={`${m.key || m.statistic}-${j}`} className="qs-comp-metric">
                            <span>{metricHeading(m, catalogue)}</span>
                            <span className="qs-dim">{Math.round((Number(m.weight) || 0) / mt * 100)}%</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {advancedOpen && (
              <div className="qs-advanced">
                <div className="qs-adv-section">
                  <div className="qs-block-title">Statistic columns {columnSet !== 'custom' && <span className="qs-muted">(preset; switch Columns to Custom to edit)</span>}</div>
                  {columnSet === 'custom'
                    ? <MetricPicker catalogue={catalogue} asAtDates={universe?.as_at_dates} metrics={customMetrics} setMetrics={setCustomMetrics} />
                    : <div className="qs-muted">{metrics.map((m) => m.statistic).join(' · ')} over {period === 'custom' ? `${fromMonth ? monthLabel(fromMonth) : '…'} to ${asAt ? monthLabel(asAt) : 'latest'}` : period}</div>}
                  {result?.metrics?.length > 0 && (
                    <div className="qs-cols" style={{ marginTop: 6 }}>
                      show:
                      {result.metrics.map((m) => (
                        <label key={m.key} className="qs-col-toggle">
                          <input type="checkbox" checked={!hiddenCols.has(m.key)} onChange={() => toggleCol(m.key)} /> {m.label} {m.period}{m.rolling ? ` r${m.rolling}` : ''}
                          {result.null_share[m.key] > 0.2 && <span className="qs-warnmark" title={`${Math.round(result.null_share[m.key] * 100)}% of the universe has no value for this column`}>!</span>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div className="qs-adv-section">
                  <div className="qs-block-title">Threshold rules</div>
                  <ThresholdEditor catalogue={catalogue} metrics={metrics} thresholds={liveThresholds} setThresholds={setThresholds} />
                  {staleRules > 0 && <div className="qs-muted">{staleRules} rule{staleRules > 1 ? 's' : ''} refer to columns not in this set and are ignored.</div>}
                  <div className="qs-toggles">
                    <label className="qs-field" title="A fund with no value in any statistic column is dropped from the results">
                      <input type="checkbox" checked={requireAll} onChange={(e) => setRequireAll(e.target.checked)} /> require a value in every statistic column
                    </label>
                    <label className="qs-field" title="Otherwise a fund missing a weighting metric is scored on the rest and flagged with the count used">
                      <input type="checkbox" checked={requireFullScore} onChange={(e) => setRequireFullScore(e.target.checked)} /> give a total score only when every weighting metric has a value
                    </label>
                  </div>
                </div>
                <div className="qs-adv-section">
                  <div className="qs-block-title">Display</div>
                  <div className="qs-toggles">
                    <label className="qs-field"><input type="checkbox" checked={sparkOn} onChange={(e) => setSparkOn(e.target.checked)} /> trend sparkline (rolling 12m active return, 3y)</label>
                    <label className="qs-field"><input type="checkbox" checked={heat} onChange={(e) => setHeat(e.target.checked)} /> heat shading by percentile</label>
                    <label className="qs-field"><input type="checkbox" checked={moreCols} onChange={(e) => setMoreCols(e.target.checked)} /> style, tilt and months columns</label>
                  </div>
                </div>
              </div>
            )}

            {error && <div className="qs-alert">{error}</div>}
            {result && result.as_at !== result.data_max_date && (
              <div className="qs-warn">Restated as at {longDate(result.as_at)}: every statistic uses returns to that month only. Sub asset classes, styles, tilts and benchmarks are as they stand today, and the universe is today&apos;s funds.</div>
            )}
            {!result && !error && <div className="qs-muted qs-editor-empty">{running ? 'Running the screen…' : customReady ? 'Choose columns and run.' : 'Choose the From month, then run.'}</div>}
            {result && (
              <>
                <ResultsGrid result={result} catalogue={catalogue} rows={rows} selected={selected} toggleSelect={toggleSelect}
                  heat={heat} sort={sort} setSort={setSort} hiddenCols={hiddenCols} moreCols={moreCols} />
                <div className="qs-under-grid">
                  <button type="button" className="qs-link" onClick={() => setChartsOpen((o) => !o)}>{chartsOpen ? '▾ Hide charts' : '▸ Show charts'}</button>
                  {selected.size > 0 && <button type="button" className="qs-link" onClick={() => setSelected(new Set())}>clear {selected.size} selected</button>}
                  {chartsOpen && (
                    <label className="qs-field">history
                      <select value={historyMonths} onChange={(e) => setHistoryMonths(Number(e.target.value))}>
                        {[12, 24, 36, 60, 120].map((m) => <option key={m} value={m}>{m} months</option>)}
                      </select>
                    </label>
                  )}
                  <span className="qs-muted">tick rows (up to 12) for the time-series charts and the Analysis tab</span>
                </div>
                {chartsOpen && <ChartPanel result={result} rows={rows} sort={sort} catalogue={catalogue} selected={selected} distribution={distribution} history={history} historyError={historyError} />}
                <div className="qs-foot">
                  Each statistic cell shows the value and the fund&apos;s percentile within its whole Sub Asset Class (100 = best, direction-aware); filters never change the cohort.
                  Statistics are null unless at least 12 observed months and 90% of the window. Universe is current funds only (survivorship).
                  Stale = the fund has not reported to the as-at month. Benchmark = AP List benchmark fund. Source: Refinitiv via Manager_Data.
                </div>
              </>
            )}
          </section>
        </div>
      )}

    </div>
  );
}
